import { Loader } from "@/components/Loader";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowRight, CircleAlert } from "lucide-react";
import { Link, useLocation } from "wouter";
import { AtRiskDealsDataTable } from "../components/AtRiskDealsDataTable";
import { AnalysisInfoTooltip, AnalysisNarrative } from "./AnalysisNarrative";
import { ForecastFilters } from "./ForecastFilters";
import { PipelineHealthStageChart } from "./PipelineAnalysisCharts";
import { formatCompactIsoCurrency } from "@/revenue-intelligence/lib/formatCurrency";
import {
  buildStateOfSalesV2Query,
  useStateOfSalesV2Forecast,
} from "./useForecastData";
import { useStateOfSalesV2PipelineData } from "./usePipelineData";

function HealthMovementWip() {
  return (
    <Card className="border-dashed shadow-none">
      <CardContent className="flex gap-3 p-5">
        <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
        <div>
          <p className="text-sm font-medium text-[#231942]">30-day Pipeline Health movement is not yet reliable</p>
          <p className="mt-1 text-xs leading-5 text-gray-500">
            Coverage, generated pipeline, progression, moved-in and moved-out value require point-in-time selected-period membership, expected close, entered value, stage probability, risk and remaining-stage timing.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export function RevenueIntelligenceStateOfSalesV2PipelineHealthPage() {
  const model = useStateOfSalesV2Forecast();
  const pipeline = useStateOfSalesV2PipelineData(model);
  const [, setLocation] = useLocation();

  if (model.isLoading || pipeline.isLoading) return <Loader />;
  if (model.error || pipeline.error || !model.forecast || !pipeline.forecastPosition) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex min-h-64 flex-col items-center justify-center gap-3 text-center">
          <CircleAlert className="h-5 w-5 text-gray-400" />
          <p className="text-sm text-gray-500">Pipeline Health data could not be loaded.</p>
          <Button variant="outline" size="sm" onClick={() => void pipeline.retry()}>Try again</Button>
        </CardContent>
      </Card>
    );
  }

  const health = pipeline.health;
  const position = pipeline.forecastPosition;
  const weakStages = health?.stageRows
    .filter(row => row.coveragePercentage < 100)
    .toSorted((a, b) => a.coveragePercentage - b.coveragePercentage) ?? [];
  const context = buildStateOfSalesV2Query(model.filters);
  const riskHref = `/revenue-intelligence/state-of-sales-v2/pipeline-risk?${context}`;
  const forecastHref = `/revenue-intelligence/state-of-sales-v2/forecast?${context}`;
  const contextParams = new URLSearchParams(context);
  contextParams.set("dealDetailsViewSource", "state-of-sales-v2-pipeline-health");

  const conclusion = position.historical
    ? "Historical Pipeline Health is unavailable without a point-in-time open-pipeline snapshot."
    : health
      ? health.coveragePercentage < 100
        ? `Pipeline Health is below target at ${(health.coveragePercentage / 100).toFixed(1)}x coverage.`
        : `Pipeline Health is sufficient at ${(health.coveragePercentage / 100).toFixed(1)}x coverage.`
      : "Pipeline Health cannot be calculated because the selected-period target is missing.";
  const explanation = position.historical
    ? "Today’s open pipeline is not used to describe a historical position."
    : health?.weakestStage && weakStages.length
      ? `${health.weakestStage.label} has the largest supported coverage gap.`
      : health
        ? "Every adjusted probability band meets the existing coverage requirement."
        : "A configured target is required before coverage can be evaluated.";
  const evidence = health
    ? `${formatCompactIsoCurrency(health.timeAdjustedPipelineValue, model.companyCurrency)} risk-, stage- and time-adjusted pipeline against ${formatCompactIsoCurrency(health.target, model.companyCurrency)} target across ${health.openDealCount} open ${health.openDealCount === 1 ? "deal" : "deals"}.`
    : "No current selected-period Pipeline Health evidence is shown.";

  return (
    <div className="space-y-5 pb-8">
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div>
          <h1 className="text-2xl font-semibold">Pipeline Health</h1>
        </div>
        <ForecastFilters model={model} />
      </div>

      <AnalysisNarrative conclusion={conclusion} explanation={explanation} evidence={evidence} />

      <section aria-labelledby="current-health" className="space-y-3">
        <h2 id="current-health" className="text-base font-semibold">Current pipeline health</h2>
        {position.historical ? (
          <Card className="border-dashed shadow-none">
            <CardContent className="p-5 text-sm text-gray-500">
              A historical view requires the open pipeline, Revelate expected close, probability, risk and stage timing as they were during the selected period.
            </CardContent>
          </Card>
        ) : health ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Card><CardHeader className="p-4"><div className="flex items-center gap-1"><CardDescription className="text-[11px]">Time-adjusted coverage</CardDescription><AnalysisInfoTooltip label="Time-adjusted coverage" text="Existing coverage uses normalized entered value, adjusted stage probability, a fixed average-month time factor and the existing Forecast risk score, divided by the selected-period target." /></div><CardTitle className="mt-1 text-lg tabular-nums">{(health.coveragePercentage / 100).toFixed(1)}x</CardTitle><p className="text-xs text-gray-500">Against target</p></CardHeader></Card>
              <Card><CardHeader className="p-4"><CardDescription className="text-[11px]">Adjusted pipeline value</CardDescription><CardTitle className="mt-1 text-lg tabular-nums">{formatCompactIsoCurrency(health.timeAdjustedPipelineValue, model.companyCurrency)}</CardTitle><p className="text-xs text-gray-500">Existing coverage basis</p></CardHeader></Card>
              <Card><CardHeader className="p-4"><CardDescription className="text-[11px]">Selected-period target</CardDescription><CardTitle className="mt-1 text-lg tabular-nums">{formatCompactIsoCurrency(health.target, model.companyCurrency)}</CardTitle><p className="text-xs text-gray-500">Same target context as Forecast</p></CardHeader></Card>
              <Card><CardHeader className="p-4"><CardDescription className="text-[11px]">Open pipeline</CardDescription><CardTitle className="mt-1 text-lg tabular-nums">{formatCompactIsoCurrency(health.rawOpenPipelineValue, model.companyCurrency)}</CardTitle><p className="text-xs text-gray-500">{health.openDealCount} {health.openDealCount === 1 ? "deal" : "deals"} · entered value</p></CardHeader></Card>
            </div>
            <Card>
              <CardHeader className="p-5 pb-0">
                <CardTitle className="text-sm">Coverage by adjusted probability band</CardTitle>
                <CardDescription className="text-xs">The dashed reference marks the existing 1.0x requirement for each band.</CardDescription>
              </CardHeader>
              <CardContent className="p-5 pt-2">
                <PipelineHealthStageChart data={health.stageRows} />
              </CardContent>
            </Card>
          </>
        ) : (
          <Card className="border-dashed shadow-none"><CardContent className="p-5 text-sm text-gray-500">Pipeline Health cannot be calculated until a selected-period target is configured.</CardContent></Card>
        )}
      </section>

      <section aria-labelledby="health-change" className="space-y-3">
        <h2 id="health-change" className="text-base font-semibold">What changed</h2>
        <HealthMovementWip />
      </section>

      {!position.historical && health && (
        <>
          <section aria-labelledby="health-drivers" className="space-y-3">
            <div>
              <h2 id="health-drivers" className="text-base font-semibold">Structural drivers</h2>
              <p className="mt-1 text-xs text-gray-500">Current stage shortages and raw pipeline concentration. Historical movement is kept separate.</p>
            </div>
            {weakStages.length ? (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {weakStages.map(stage => (
                  <Card key={stage.key}>
                    <CardHeader className="p-4">
                      <CardDescription className="text-[11px]">Stage coverage shortage</CardDescription>
                      <CardTitle className="mt-1 text-sm">{stage.label}</CardTitle>
                      <p className="text-base tabular-nums text-[#231942]">{stage.coveragePercentage.toFixed(1)}%</p>
                      <p className="text-xs text-gray-500">
                        {stage.requiredValue > 0
                          ? `${formatCompactIsoCurrency(stage.expectedValue, model.companyCurrency)} adjusted pipeline · ${formatCompactIsoCurrency(stage.requiredValue, model.companyCurrency)} required pipeline`
                          : "No selected-period open pipeline in this band."}
                      </p>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-[#1D819A]/30 bg-[#1D819A]/[0.025] shadow-none"><CardContent className="p-5 text-sm text-gray-600">Every adjusted probability band meets the existing coverage requirement.</CardContent></Card>
            )}
            {health.concentrationDealCount > 0 && (
              <Card>
                <CardHeader className="p-5 pb-2">
                  <CardTitle className="text-sm">Pipeline concentration</CardTitle>
                  <CardDescription className="text-xs">Raw normalized entered value, separate from time-adjusted coverage.</CardDescription>
                </CardHeader>
                <CardContent className="p-5 pt-2">
                  <p className="text-sm text-gray-700">{health.concentrationDealCount} {health.concentrationDealCount === 1 ? "deal represents" : "deals represent"} at least half of selected-period open-pipeline value. Concentration is {health.concentrationLabel}.</p>
                </CardContent>
              </Card>
            )}
          </section>

          <section aria-labelledby="health-attention" className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <h2 id="health-attention" className="text-base font-semibold">Management attention</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" asChild><Link href={riskHref}>Open Pipeline Risk <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link></Button>
                <Button variant="outline" size="sm" asChild><Link href={forecastHref}>Open Forecast <ArrowRight className="ml-1.5 h-3.5 w-3.5" /></Link></Button>
              </div>
            </div>
            <Card>
              <CardHeader className="p-5 pb-3"><CardTitle className="text-sm">Coverage priorities</CardTitle><CardDescription className="text-xs">Probability bands ranked from lowest current coverage.</CardDescription></CardHeader>
              <CardContent className="space-y-3 p-5 pt-0">
                {weakStages.length ? weakStages.map(stage => (
                  <div key={stage.key} className="flex flex-col justify-between gap-2 border-b border-gray-100 pb-3 last:border-0 last:pb-0 sm:flex-row sm:items-center">
                    <div><p className="text-sm font-medium text-[#231942]">{stage.label}</p><p className="mt-1 text-xs text-gray-500">Review pipeline creation and progression in this band.</p></div>
                    <p className="shrink-0 text-sm tabular-nums text-gray-700">{stage.coveragePercentage.toFixed(1)}% coverage</p>
                  </div>
                )) : <p className="text-sm text-gray-500">No stage-coverage intervention is indicated by the existing helper.</p>}
              </CardContent>
            </Card>
            {health.concentrationDeals.length > 0 && (
              <Card>
                <CardHeader className="p-5 pb-0"><CardTitle className="text-sm">Concentration evidence</CardTitle><CardDescription className="text-xs">The five largest deals within the set that makes up at least half of raw selected-period open-pipeline value.</CardDescription></CardHeader>
                <CardContent className="px-5 pb-1">
                  <AtRiskDealsDataTable deals={health.concentrationDeals} showTitle={false} showExpectedCloseDate onClickDeal={(dealId) => setLocation(`/revenue-intelligence/deal/${dealId}?${contextParams.toString()}`)} />
                </CardContent>
              </Card>
            )}
          </section>
        </>
      )}
    </div>
  );
}
